import React, { useCallback, useState } from "react";
import DadosEstadoCivilCompradorGalleriaForm from "./DadosEstadoCivilCompradorGalleriaForm";
import { useDispatch, useSelector } from "react-redux";
import {
  getPreenchimentoOperacao,
  isCarregandoConjuge,
  isGravandoPreenchimentoOperacao,
} from "../../../../../selectors/operacao.selectors";
import useCompleteForm from "../../../../../hooks/useCompleteForm";
import validators from "../../../../../utils/validators";
import { actions } from "../../../../../actions/operacao.actions";
import { removeNonDigitsFromString } from "../../../../../utils/basic";

const DadosEstadoCivilCompradorGalleriaFormConnected = ({
  setStep,
  eventsCompradorPf,
  setParticipante,
  participante: comprador,
  ...otherProps
}) => {
  const dispatch = useDispatch();
  const {
    operacao,
    dominios: { estadoCivil: estadosCivis, regimeComunhao: regimesComunhao },
  } = useSelector(getPreenchimentoOperacao);
  const loadingConjuge = useSelector(isCarregandoConjuge);
  const submitting = useSelector(isGravandoPreenchimentoOperacao);

  const { conjuge, uniaoEstavel } =
    estadosCivis.find((e) => e.id === comprador?.estadoCivil?.id) ?? {};
  const [exibeConjuge, setExibeConjuge] = useState(
    (comprador?.uniaoEstavel || conjuge) ?? false
  );
  const [exibeUniaoEstavel, setExibeUniaoEstavel] = useState(
    uniaoEstavel ?? false
  );

  const callback = useCallback(
    (data) => {
      const c = data.operacao.participantes.find((p) => p.id === comprador.id);
      if (!!c) {
        setParticipante({
          ...c,
          fluxo: data.validacao.completa.participantes.find(
            (f) => f.id === c?.id
          )?.etapas,
        });
      }
      setStep(0);
    },
    [setStep, setParticipante, comprador]
  );

  const [formProps, handleSubmit] = useCompleteForm({
    rules: (form) => ({
      estadoCivil: validators.object({ required: true }),
      conjuge: {
        nome: validators.string({
          custom: {
            requiredIf: (value) => {
              const uniaoEstavel = form.getValues("uniaoEstavel");

              if (!exibeConjuge && !uniaoEstavel) {
                return true;
              }

              if (value !== undefined && value !== null && value !== "")
                return true;
              return "Campo obrigatório";
            },
            nomeCompleto: (value) =>
              !value ||
              (value.indexOf(" ") > 0 &&
                value.substring(value.indexOf(" ") + 1).length > 0) ||
              "Nome informado deve ser completo",
          },
        }),
        cpfCnpj: validators.cpf({
          custom: {
            requiredIf: (value) => {
              const uniaoEstavel = form.getValues("uniaoEstavel");

              if (!exibeConjuge && !uniaoEstavel) {
                return true;
              }

              if (value !== undefined && value !== null && value !== "")
                return true;
              return "Campo obrigatório";
            },
          },
        }),
      },
    }),
    initialValues: useCallback(
      () => ({
        estadoCivil: null,
        uniaoEstavel: false,
        regimeComunhao: null,
        ...comprador,
        conjuge: {
          nome: "",
          cpfCnpj: "",
          ...comprador?.conjuge,
        },
      }),
      [comprador]
    ),
    handleSubmit: useCallback(
      (values) => {
        dispatch(
          actions.gravarPreenchimento.request({
            callback,
            instituicao: operacao.codigoInstituicao,
            passo: "dados-estado-civil",
            participante: {
              id: comprador?.id,
              idEstadoCivil: values.estadoCivil?.id,
              uniaoEstavel: exibeUniaoEstavel && values.uniaoEstavel,
              idRegimeComunhao: !!exibeConjuge
                ? values.regimeComunhao?.id
                : null,
              conjuge: !!exibeConjuge
                ? {
                    nome: values.conjuge.nome,
                    cpfCnpj: removeNonDigitsFromString(values.conjuge.cpfCnpj),
                  }
                : null,
            },
            operacao: {
              id: operacao.id,
            },
          })
        );
      },
      [dispatch, comprador, callback, operacao, exibeConjuge, exibeUniaoEstavel]
    ),
  });
  const { setValue } = formProps;

  const handleEstadoCivilChange = useCallback(
    (v) => {
      const vaiMudar =
        !exibeConjuge && !exibeUniaoEstavel
          ? true
          : exibeUniaoEstavel ^ (v?.uniaoEstavel ?? false);

      if (vaiMudar) {
        setValue("uniaoEstavel", false);
        setExibeUniaoEstavel(v?.uniaoEstavel ?? false);
        setExibeConjuge(v?.conjuge ?? false);
      }
    },
    [
      setExibeConjuge,
      setExibeUniaoEstavel,
      exibeUniaoEstavel,
      exibeConjuge,
      setValue,
    ]
  );
  const handleUniaoEstavelChange = useCallback(
    (e) => {
      setExibeConjuge(e.target.checked);
    },
    [setExibeConjuge]
  );

  const successCallback = useCallback(
    (data) => {
      if (!!data) {
        setValue("conjuge.nome", data.nome);
      }
    },
    [setValue]
  );
  const handleCpfCnpjChange = useCallback(
    (v) => {
      if (!!v) {
        dispatch(
          actions.obterConjuge.request({
            cpfCnpj: removeNonDigitsFromString(v),
            parceiro: operacao.idParceiro,
            callback: successCallback,
          })
        );
      }
    },
    [dispatch, operacao, successCallback]
  );

  return (
    <DadosEstadoCivilCompradorGalleriaForm
      {...otherProps}
      estadosCivis={estadosCivis}
      regimesComunhao={regimesComunhao}
      events={eventsCompradorPf}
      fluxo={comprador.fluxo}
      formProps={formProps}
      handleEstadoCivilChange={handleEstadoCivilChange}
      handleSubmit={handleSubmit}
      exibeConjuge={exibeConjuge}
      exibeUniaoEstavel={exibeUniaoEstavel}
      handleUniaoEstavelChange={handleUniaoEstavelChange}
      submitting={submitting}
      loadingConjuge={loadingConjuge}
      handleCpfCnpjChange={handleCpfCnpjChange}
    />
  );
};

export default DadosEstadoCivilCompradorGalleriaFormConnected;
